/**
 * Achat EXPRESS d'un pack ou d'un programme — `/debloquer/{pack|programme}/{packId}`.
 *
 * Ouverte par les boutons « Débloquer » des pages programme / pack du site :
 * pas de détour par la page d'une carte ni de nouvelles offres. Sans compte →
 * connexion / inscription sur place (retour ici, même après l'onboarding) ;
 * connecté → départ DIRECT vers le paiement Stripe. Le retour de paiement se
 * fait sur la page d'achat du pack (`fromPackId`), qui confirme l'achat.
 * Page volontairement courte : l'offre, puis la connexion ou le paiement.
 *
 * Publique (enfant de rootRoute). Prix calculés par la base (`get_pack_offers`).
 */
import { useEffect, useRef, useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { ArrowLeft, Loader2, Lock } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import { formatEuros, offerContentLabel } from '@jeraisonne/db/cards';
import { levelLabel, subjectLabel } from '@jeraisonne/db/curriculum';
import { useAuth } from '@/lib/auth';
import { OtpAuthForm } from '@/components/otp-auth-form';
import { setReturnTo } from '@/lib/return-to';
import { isActiveTab } from '@/lib/active-tab';
import { SkillcardPromoBanner } from '@/features/skillcards/skillcard-promo-banner';
import {
  packBuyPath,
  skillcardCheckoutErrorMessage,
  startSkillcardCheckout,
  usePackOffers,
} from '@/lib/skillcards/purchase';
import {
  expressBuyPath,
  expressQuote,
  expressReturnPath,
  expressUnlockedLabel,
  isExpressOffer,
  type ExpressOffer,
} from '@/lib/skillcards/express-buy';

export function ExpressBuyPage() {
  const params = useParams({ strict: false }) as { offer: string; packId: string };
  const offer: ExpressOffer = isExpressOffer(params.offer) ? params.offer : 'pack';
  const packId = params.packId;
  const { session, profile, profileLoading } = useAuth();
  const userId = session?.user?.id ?? null;
  const offers = usePackOffers(packId, userId);
  const data = offers.data ?? null;
  const quote = data ? expressQuote(data, offer) : null;
  const signedIn = !!session && !!profile;
  // Un parent n'achète pas pour lui : il offre le lot à un enfant (0326).
  const isParent = profile?.role === 'parent';
  const unlocked = data && quote ? expressUnlockedLabel(offer, quote, data.access) : null;
  const selfPath = expressBuyPath(offer, packId);
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (data && quote) document.title = `Débloquer ${quote.label} · JeRaisonne`;
  }, [data, quote]);

  // Sans compte : on revient ici après la connexion (OAuth, onboarding compris).
  useEffect(() => {
    if (!session && isActiveTab()) setReturnTo(selfPath);
  }, [session, selfPath]);

  // Nouveau compte (session sans profil) : onboarding, puis retour ici.
  useEffect(() => {
    if (!session || profileLoading || profile || !isActiveTab()) return;
    setReturnTo(selfPath);
    window.location.href = '/onboarding';
  }, [session, profileLoading, profile, selfPath]);

  async function pay() {
    const cardId = data?.cards[0]?.id;
    if (!data || !quote || !cardId) return;
    setError(null);
    started.current = true;
    try {
      window.location.href = await startSkillcardCheckout({
        kind: offer,
        targetId: quote.target_id,
        cardId,
        returnPath: expressReturnPath(profile?.role),
        fromPackId: data.pack.id,
      });
    } catch (e) {
      setError(skillcardCheckoutErrorMessage((e as Error).message));
    }
  }

  // Connecté et rien de déjà débloqué : départ direct vers le paiement.
  useEffect(() => {
    if (started.current || !signedIn || isParent || !data || !quote || unlocked) return;
    if (!isActiveTab()) return;
    void pay();
    // `pay` lit l'état courant ; on ne déclenche qu'une fois
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn, isParent, data, quote, unlocked]);

  const backHref = isParent ? '/parent' : profile ? expressReturnPath(profile.role) : '/';

  return (
    <div className="native:pt-[calc(env(safe-area-inset-top)+0.75rem)] min-h-dvh px-4 pb-[max(env(safe-area-inset-bottom),1.5rem)] pt-3 sm:py-8">
      <div className="mx-auto max-w-xl space-y-4">
        <a
          href={backHref}
          className="text-ink-500 hover:text-ink-800 inline-flex min-h-[2.75rem] items-center gap-1.5 text-sm"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden /> Retour
        </a>

        {offers.isLoading ? (
          <div className="bg-ink-100 h-40 animate-pulse rounded-2xl" />
        ) : !data || !quote ? (
          <p className="text-ink-600 text-sm">Cette offre n’est pas disponible.</p>
        ) : (
          <>
            <SkillcardPromoBanner />
            <div
              data-testid="express-offer"
              className="border-primary-200 bg-primary-50/50 ocean:bg-white/5 rounded-2xl border-2 p-5"
            >
              <p className="text-primary-700 text-sm font-bold uppercase tracking-wider">
                {offer === 'pack' ? 'Le pack entier' : 'Tout le programme de l’année'}
              </p>
              <p className="text-ink-900 font-display mt-1 text-lg font-bold">
                {offer === 'pack'
                  ? quote.label
                  : `${levelLabel(data.pack.level)} · ${subjectLabel(data.pack.subject)}`}
              </p>
              <p className="mt-1 flex flex-wrap items-baseline gap-x-2">
                <span className="text-ink-900 font-display text-3xl font-extrabold">
                  {formatEuros(quote.price_cents)}
                </span>
                {quote.unit_total_cents > quote.price_cents && (
                  <span className="text-ink-400 text-sm line-through">
                    {formatEuros(quote.unit_total_cents)}
                  </span>
                )}
                {quote.promo && (
                  <span className="bg-accent-500 rounded-full px-2 py-0.5 text-xs font-bold text-[#061433]">
                    Promo −{quote.promo.percent}&nbsp;%
                  </span>
                )}
              </p>
              <p className="text-ink-600 mt-1 text-sm">
                {offerContentLabel(quote)} · accès {data.settings.validity_months} mois · paiement
                unique, sans abonnement
              </p>
            </div>

            {unlocked ? (
              <div className="bg-success-50 text-success-800 space-y-3 rounded-2xl p-4 text-sm">
                <p className="font-semibold">{unlocked}.</p>
                <a href={backHref} className="font-semibold underline">
                  Aller à mes SKILLCARDS
                </a>
              </div>
            ) : isParent ? (
              <div className="border-ink-200 ocean:bg-white/5 space-y-3 rounded-2xl border bg-white p-4">
                <p className="text-ink-900 font-display text-base font-bold">
                  Offre-le à ton enfant
                </p>
                <p className="text-ink-600 text-sm">
                  Tu as un compte parent : choisis l’enfant qui recevra ces SKILLCARDS, puis passe
                  au paiement.
                </p>
                <a
                  href="/parent/skillcards"
                  className="bg-primary-600 hover:bg-primary-700 inline-flex min-h-[2.75rem] w-full items-center justify-center rounded-xl px-6 font-semibold text-white active:scale-[0.98]"
                >
                  Choisir mon enfant
                </a>
              </div>
            ) : !session ? (
              <div className="border-ink-200 ocean:bg-white/5 space-y-3 rounded-2xl border bg-white p-4">
                <OtpAuthForm
                  initialMode="signup"
                  renderHeader={() => (
                    <div className="space-y-1">
                      <p className="text-ink-900 font-display text-base font-bold">
                        <Lock className="mr-1 inline h-4 w-4" aria-hidden />
                        Crée ton compte ou connecte-toi
                      </p>
                      <p className="text-ink-600 text-sm">
                        Puis tu passes directement au paiement sécurisé.
                      </p>
                    </div>
                  )}
                />
              </div>
            ) : error ? (
              <div className="space-y-3">
                <p role="alert" className="text-error-600 text-sm">
                  {error}
                </p>
                <Button fullWidth onClick={() => void pay()}>
                  Réessayer le paiement
                </Button>
                <a
                  href={packBuyPath(packId, expressReturnPath(profile?.role))}
                  className="text-primary-600 block min-h-[2.75rem] text-center text-sm font-semibold"
                >
                  Voir toutes les offres
                </a>
              </div>
            ) : (
              <p
                role="status"
                className="text-ink-600 flex min-h-[2.75rem] items-center justify-center gap-2 text-sm"
              >
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Redirection vers le paiement sécurisé…
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
